import { useMemo, useState } from 'react';
import { Container } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { ProductModel } from '../../models/product/product';
import { productBullets, savingsPercent, unitPriceFor } from '../../../api/product';
import { resolveImage } from '../../../api/axiosIntance';
import { addToCart, openCart } from '../../../redux/cart';
import { RootState } from '../../../redux';
import Reveal from '../../atome/motion/reveal';
import { Eyebrow } from '../../atome/motion/motion.styled';
import {
  Blurb,
  Body,
  Card,
  Category,
  CollectionSection,
  CountBadge,
  EmptyState,
  FilterChip,
  Filters,
  Grid,
  GridItem,
  Media,
  Name,
  PriceRow,
  QuickAdd,
  Ribbon,
  SectionHead,
} from './collection.styled';

const FALLBACK_IMAGE = '/img/klarity-product.jpg';

interface CollectionProps {
  products: ProductModel[];
  eyebrow?: string;
  heading?: string;
  blurb?: string;
  /** Category chips are hidden on secondary strips (e.g. "you may also like"). */
  showFilters?: boolean;
}

/**
 * Catalogue layout used whenever the shop carries more than one product.
 * The first card runs wide on desktop so the grid reads as an editorial spread.
 */
const Collection = ({
  products,
  eyebrow = 'The collection',
  heading = 'Considered care, piece by piece',
  blurb = 'Every formula is made in small batches with naturally derived actives — chosen for what they do, not for how they sound on a label.',
  showFilters = true,
}: CollectionProps) => {
  const dispatch = useDispatch();
  const userEmail = useSelector((state: RootState) => state.user.email);
  const hasEmail = Boolean(userEmail);

  const categories = useMemo(() => {
    const unique = Array.from(new Set(products.map((p) => p.category).filter(Boolean)));
    return ['all', ...unique];
  }, [products]);

  const [filter, setFilter] = useState('all');

  const visible = useMemo(
    () => (filter === 'all' ? products : products.filter((p) => p.category === filter)),
    [products, filter]
  );

  // The only action on a card: drop one straight in the bag, so several
  // products can be collected without leaving the grid.
  const quickAdd = (event: React.MouseEvent, product: ProductModel) => {
    event.stopPropagation();
    dispatch(addToCart({ product, quantity: 1 }));
    dispatch(openCart());
  };

  return (
    <CollectionSection id="product-section">
      <Container maxWidth="lg">
        <Reveal from="up">
          <SectionHead>
            <div>
              <Eyebrow>{eyebrow}</Eyebrow>
              <h2>{heading}</h2>
              <p>{blurb}</p>
            </div>
            <CountBadge>
              {products.length} {products.length === 1 ? 'product' : 'products'}
            </CountBadge>
          </SectionHead>
        </Reveal>

        {showFilters && categories.length > 2 && (
          <Reveal from="up" delay={80}>
            <Filters>
              {categories.map((category) => (
                <FilterChip
                  key={category}
                  $active={filter === category}
                  onClick={() => setFilter(category)}
                >
                  {category}
                </FilterChip>
              ))}
            </Filters>
          </Reveal>
        )}

        {visible.length === 0 ? (
          <EmptyState>
            <h3>Nothing here yet</h3>
            <p>Try another category — new formulas are on their way.</p>
          </EmptyState>
        ) : (
          <Grid>
            {visible.map((product, index) => {
              const image = resolveImage(product.imageUrl?.[0]) || FALLBACK_IMAGE;
              const unit = unitPriceFor(product, hasEmail);
              const saving = savingsPercent(product, hasEmail);
              const blurb = productBullets(product.description)[0] || '';
              const featured = index === 0 && visible.length > 2;
              const soldOut = product.stock === 0;

              return (
                // `filter` in the key replays the entrance when the category changes.
                <GridItem key={`${filter}-${product.id}`} $featured={featured}>
                  <Reveal from="up" delay={Math.min(index * 90, 450)}>
                    {/* The card used to open /product/:id on click. Restore by
                        putting back: onClick={() => openProduct(product)}
                        role="button" tabIndex={0} and the Enter handler. */}
                    <Card>
                      <Media $featured={featured}>
                        <img
                          src={image}
                          alt={product.name}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = FALLBACK_IMAGE;
                          }}
                        />
                        {product.discount > 0 && <Ribbon>-{product.discount}%</Ribbon>}
                        {product.discount === 0 && soldOut && (
                          <Ribbon $tone="muted">Sold out</Ribbon>
                        )}
                        <QuickAdd onClick={(e) => quickAdd(e, product)} disabled={soldOut}>
                          {soldOut ? 'Sold out' : 'Add to bag'}
                        </QuickAdd>
                      </Media>

                      <Body>
                        {product.category && <Category>{product.category}</Category>}
                        <Name>{product.name}</Name>
                        {blurb && <Blurb>{blurb}</Blurb>}
                        <PriceRow>
                          <strong>${unit.toFixed(2)}</strong>
                          {saving > 0 && <s>${product.price.toFixed(2)}</s>}
                          {product.stock > 0 && product.stock <= 5 && (
                            <em>Only {product.stock} left</em>
                          )}
                        </PriceRow>
                      </Body>
                    </Card>
                  </Reveal>
                </GridItem>
              );
            })}
          </Grid>
        )}
      </Container>
    </CollectionSection>
  );
};

export default Collection;
